import React from 'react';
import { TEXT_MUTED, BG_SURFACE, BORDER_LIGHT } from './designTokens';

export interface TableFilters {
  zones: string[];
  statuses: string[];
  minEmptySeats: number | null;
  reservedGroup: string;
}

interface FilterControlsProps {
  filters: TableFilters;
  onChange: (filters: TableFilters) => void;
  availableZones: { name: string; colorCode: string }[];
}

const STATUSES = ['Available', 'PartiallyReserved', 'FullyReserved', 'Held', 'Blocked'];

const STATUS_LABELS: Record<string, string> = {
  Available: 'Available',
  PartiallyReserved: 'Partially Reserved',
  FullyReserved: 'Fully Reserved',
  Held: 'Held',
  Blocked: 'Blocked',
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    padding: '0.75rem 1rem',
    backgroundColor: BG_SURFACE,
    borderRadius: '6px',
    border: `1px solid ${BORDER_LIGHT}`,
    marginBottom: '1rem',
  },
  group: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  label: {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: TEXT_MUTED,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  select: {
    padding: '0.4rem 0.6rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.85rem',
    minWidth: '140px',
  },
  input: {
    padding: '0.4rem 0.6rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.85rem',
    width: '120px',
  },
  numberInput: {
    padding: '0.4rem 0.6rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.85rem',
    width: '80px',
  },
  clearBtn: {
    padding: '0.4rem 0.75rem',
    backgroundColor: '#f5f5f5',
    color: '#555',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.8rem',
    cursor: 'pointer',
  },
};

export function FilterControls({ filters, onChange, availableZones }: FilterControlsProps) {
  const handleZoneChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = Array.from(e.target.selectedOptions, (opt) => opt.value);
    onChange({ ...filters, zones: selected });
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = Array.from(e.target.selectedOptions, (opt) => opt.value);
    onChange({ ...filters, statuses: selected });
  };

  const handleMinSeatsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    onChange({ ...filters, minEmptySeats: val ? parseInt(val, 10) : null });
  };

  const handleGroupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...filters, reservedGroup: e.target.value });
  };

  const handleClear = () => {
    onChange({ zones: [], statuses: [], minEmptySeats: null, reservedGroup: '' });
  };

  const hasFilters =
    filters.zones.length > 0 ||
    filters.statuses.length > 0 ||
    filters.minEmptySeats !== null ||
    filters.reservedGroup.trim() !== '';

  return (
    <div style={styles.container} role="group" aria-label="Table filters">
      <div style={styles.group}>
        <label style={styles.label}>Zone</label>
        <select
          style={styles.select}
          multiple
          size={1}
          value={filters.zones}
          onChange={handleZoneChange}
          aria-label="Filter by zone"
        >
          {availableZones.map((zone) => (
            <option key={zone.name} value={zone.name}>
              {zone.name}
            </option>
          ))}
        </select>
      </div>

      <div style={styles.group}>
        <label style={styles.label}>Status</label>
        <select
          style={styles.select}
          multiple
          size={1}
          value={filters.statuses}
          onChange={handleStatusChange}
          aria-label="Filter by status"
        >
          {STATUSES.map((status) => (
            <option key={status} value={status}>
              {STATUS_LABELS[status]}
            </option>
          ))}
        </select>
      </div>

      <div style={styles.group}>
        <label style={styles.label}>Min Empty Seats</label>
        <input
          type="number"
          style={styles.numberInput}
          min={1}
          max={10}
          value={filters.minEmptySeats ?? ''}
          onChange={handleMinSeatsChange}
          placeholder="Any"
          aria-label="Minimum empty seats"
        />
      </div>

      <div style={styles.group}>
        <label style={styles.label}>Reserved Group</label>
        <input
          type="text"
          style={styles.input}
          value={filters.reservedGroup}
          onChange={handleGroupChange}
          placeholder="Search group..."
          aria-label="Filter by reserved group name"
        />
      </div>

      {hasFilters && (
        <button style={styles.clearBtn} onClick={handleClear} aria-label="Clear all filters">
          Clear Filters
        </button>
      )}
    </div>
  );
}
